import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { Plus, Minus, Maximize, RefreshCw } from 'lucide-react';
// Node Types
type NodeType = 'main' | 'sub' | 'partner' | 'audit' | 'action';
interface Node {
  id: string;
  label: string;
  type: NodeType;
  x: number;
  y: number;
  width?: number;
  height?: number;
  connections?: string[]; // IDs of nodes this node connects TO
}
// Data based on the uploaded images
const NODES: Node[] = [
// --- MAIN SPINE ---
{
  id: 'job-boards',
  label: 'Job Boards',
  type: 'main',
  x: 50,
  y: 300
}, {
  id: 'ats',
  label: 'Applicant Tracking\nSystem',
  type: 'main',
  x: 250,
  y: 300
}, {
  id: 'prospect-sys',
  label: 'Prospect Tracking\nSystem',
  type: 'main',
  x: 450,
  y: 300
}, {
  id: 'pre-screening',
  label: 'Pre-Screening',
  type: 'main',
  x: 650,
  y: 300
}, {
  id: 'screening-call',
  label: 'Screening Call',
  type: 'main',
  x: 850,
  y: 300
}, {
  id: 'interview',
  label: 'Interview/Hiring',
  type: 'main',
  x: 1050,
  y: 300
}, {
  id: 'onboarding',
  label: 'Onboarding',
  type: 'main',
  x: 1250,
  y: 300
}, {
  id: 'integration',
  label: 'Integration',
  type: 'main',
  x: 1450,
  y: 300
}, {
  id: 'sales-school',
  label: 'Sales School',
  type: 'main',
  x: 1650,
  y: 300
}, {
  id: 'day-3-7',
  label: 'Day 3-7',
  type: 'main',
  x: 1850,
  y: 300
}, {
  id: 'retention',
  label: 'RETENTION',
  type: 'partner',
  x: 2050,
  y: 300
},
// --- BRANCHES ---
// Job Boards Branch
{
  id: 'account-creation',
  label: 'Account Creation',
  type: 'sub',
  x: 50,
  y: 150
}, {
  id: 'account-mgmt',
  label: 'Account\nManagement',
  type: 'sub',
  x: 50,
  y: 50
},
// ATS Branch
{
  id: 'ats-build',
  label: 'ATS Build',
  type: 'sub',
  x: 250,
  y: 150
}, {
  id: 'resume-filter',
  label: 'Resume Filtering',
  type: 'sub',
  x: 250,
  y: 50
},
// Prospect System Branch
{
  id: 'pts-build',
  label: 'PTS Build',
  type: 'sub',
  x: 450,
  y: 150
}, {
  id: 'video-response',
  label: 'Video/Audio\nResponse',
  type: 'sub',
  x: 450,
  y: 50
},
// Pre-Screening Branch
{
  id: 'screening',
  label: 'Screening',
  type: 'sub',
  x: 650,
  y: 150
}, {
  id: 'text-verify',
  label: 'Text Verification',
  type: 'sub',
  x: 650,
  y: 50
},
// Interview Branch
{
  id: 'scripts',
  label: 'Scripts',
  type: 'sub',
  x: 1050,
  y: 150
}, {
  id: 'interview-step',
  label: 'Interview',
  type: 'sub',
  x: 1050,
  y: 50
}, {
  id: 'questions',
  label: 'Questions',
  type: 'action',
  x: 1050,
  y: -50
}, {
  id: 'commitment',
  label: 'Commitment',
  type: 'action',
  x: 1050,
  y: -150
}, {
  id: 'hiring-moment',
  label: 'Hiring Moment',
  type: 'action',
  x: 1050,
  y: -250
},
// Onboarding Branch
{
  id: 'onboarding-form',
  label: 'Onboarding Form',
  type: 'sub',
  x: 1250,
  y: 150
}, {
  id: 'welcome-email',
  label: 'Welcome Email',
  type: 'sub',
  x: 1250,
  y: 50
}, {
  id: 'script-green',
  label: 'Script',
  type: 'action',
  x: 1250,
  y: -50
}, {
  id: 'expectations',
  label: 'Expectations\nVideo',
  type: 'sub',
  x: 1250,
  y: -150
}, {
  id: 'manager-meet',
  label: 'Meeting with\nmanager',
  type: 'action',
  x: 1250,
  y: -250
},
// Integration Branch
{
  id: 'hire-day',
  label: 'Hire Day',
  type: 'action',
  x: 1450,
  y: 150
}, {
  id: 'next-day',
  label: 'Next-Day Check',
  type: 'action',
  x: 1450,
  y: 50
}, {
  id: 'progress',
  label: 'Progress Check',
  type: 'action',
  x: 1450,
  y: -50
}, {
  id: 'pre-launch',
  label: 'Pre-Launch',
  type: 'action',
  x: 1450,
  y: -150
},
// Sales School Branch
{
  id: 'weekend',
  label: 'Weekend\nHomework',
  type: 'sub',
  x: 1650,
  y: 150
}, {
  id: 'monday',
  label: 'Monday',
  type: 'sub',
  x: 1750,
  y: 50
}, {
  id: 'tuesday',
  label: 'Tuesday',
  type: 'sub',
  x: 1850,
  y: 50
},
// Day 3-7 Branch
{
  id: 'shadowing',
  label: 'Shadowing',
  type: 'action',
  x: 1850,
  y: 150
}, {
  id: 'knocking',
  label: 'Knocking',
  type: 'action',
  x: 1850,
  y: 50
}, {
  id: 'personal-conf',
  label: 'Personal\nConferences',
  type: 'action',
  x: 1850,
  y: -50
},
// Partner/Coaching Nodes (Floating)
{
  id: 'hire-coaching',
  label: 'HIRE\nCoaching',
  type: 'partner',
  x: 900,
  y: -100
}, {
  id: 'hire-auditing',
  label: 'HIRE\nAuditing',
  type: 'audit',
  x: 1550,
  y: 100
}];
// Connections (Source -> Target)
const CONNECTIONS = [
// Main Spine
['job-boards', 'ats'], ['ats', 'prospect-sys'], ['prospect-sys', 'pre-screening'], ['pre-screening', 'screening-call'], ['screening-call', 'interview'], ['interview', 'onboarding'], ['onboarding', 'integration'], ['integration', 'sales-school'], ['sales-school', 'day-3-7'], ['day-3-7', 'retention'],
// Branches (Upwards mostly in diagram, but logic flows down usually. Diagram shows arrows UP from main nodes? No, arrows point TO main nodes or FROM main nodes?
// Looking at images:
// Job Boards -> Account Creation (Up arrow?) No, Account Creation -> Account Management -> Ad Management -> Copywriting -> Job Boards?
// Actually, the arrows in the first image point UP from "Job Boards" to "Account Creation" is WRONG.
// Image 1: "Account Creation" -> "Account Management" -> "Ad Management". And "Job Boards" is at the bottom.
// Wait, the main spine is at the bottom in some images?
// Let's look at "Done For You" overview image.
// Main spine is horizontal: Job Boards -> ATS -> Prospect -> Pre-Screening -> Screening Call -> Interview -> Onboarding -> Integration -> Sales School -> Day 3-7 -> Retention.
// Branches go UP and DOWN from this spine.
// Example: ATS -> ATS Build (Up).
// Example: Interview -> Scripts (Up) -> Interview (Up) -> Final Interview (Up).
// Actually, let's just connect them visually based on proximity and logic.
// I'll use simple vertical connections for branches.
['job-boards', 'account-creation'], ['account-creation', 'account-mgmt'], ['ats', 'ats-build'], ['ats-build', 'resume-filter'], ['prospect-sys', 'pts-build'], ['pts-build', 'video-response'], ['pre-screening', 'screening'], ['screening', 'text-verify'], ['interview', 'scripts'], ['scripts', 'interview-step'], ['interview-step', 'questions'], ['questions', 'commitment'], ['commitment', 'hiring-moment'], ['onboarding', 'onboarding-form'], ['onboarding-form', 'welcome-email'], ['welcome-email', 'script-green'], ['script-green', 'expectations'], ['expectations', 'manager-meet'], ['integration', 'hire-day'], ['hire-day', 'next-day'], ['next-day', 'progress'], ['progress', 'pre-launch'], ['sales-school', 'weekend'], ['weekend', 'monday'], ['monday', 'tuesday'], ['day-3-7', 'shadowing'], ['shadowing', 'knocking'], ['knocking', 'personal-conf']];
export function SystemDiagram() {
  const [scale, setScale] = useState(0.6);
  const constraintsRef = useRef(null);
  const handleZoomIn = () => setScale(Math.min(scale + 0.1, 2));
  const handleZoomOut = () => setScale(Math.max(scale - 0.1, 0.2));
  const handleReset = () => setScale(0.6);
  return <div className="w-full h-full bg-[#0B1120] relative overflow-hidden rounded-xl border border-slate-800" data-id="element-329">
      {/* Controls */}
      <div className="absolute bottom-4 right-4 z-20 flex flex-col gap-2" data-id="element-330">
        <button onClick={handleZoomIn} className="p-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg border border-slate-700 transition-colors" data-id="element-331">
          <Plus size={20} data-id="element-332" />
        </button>
        <button onClick={handleZoomOut} className="p-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg border border-slate-700 transition-colors" data-id="element-333">
          <Minus size={20} data-id="element-334" />
        </button>
        <button onClick={handleReset} className="p-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg border border-slate-700 transition-colors" data-id="element-335">
          <RefreshCw size={20} data-id="element-336" />
        </button>
      </div>

      {/* Legend */}
      <div className="absolute top-4 left-4 z-20 bg-slate-900/90 backdrop-blur-sm p-4 rounded-xl border border-slate-800" data-id="element-337">
        <h3 className="text-white font-bold mb-3 text-sm" data-id="element-338">System Components</h3>
        <div className="space-y-2" data-id="element-339">
          <div className="flex items-center" data-id="element-340">
            <div className="w-3 h-3 rounded bg-slate-600 mr-2" data-id="element-341" />
            <span className="text-xs text-slate-300" data-id="element-342">
              Infrastructure (Done For You)
            </span>
          </div>
          <div className="flex items-center" data-id="element-343">
            <div className="w-3 h-3 rounded bg-emerald-500 mr-2" data-id="element-344" />
            <span className="text-xs text-slate-300" data-id="element-345">
              Execution (Done With You)
            </span>
          </div>
          <div className="flex items-center" data-id="element-346">
            <div className="w-3 h-3 rounded bg-[#1e293b] border border-slate-600 mr-2" data-id="element-347" />
            <span className="text-xs text-slate-300" data-id="element-348">System Assets</span>
          </div>
          <div className="flex items-center" data-id="element-349">
            <div className="w-3 h-3 rounded bg-amber-400 mr-2" data-id="element-350" />
            <span className="text-xs text-slate-300" data-id="element-351">Coaching & Retention</span>
          </div>
        </div>
      </div>

      {/* Canvas */}
      <motion.div ref={constraintsRef} className="w-full h-full cursor-grab active:cursor-grabbing" data-id="element-352">
        <motion.div drag dragConstraints={{
        left: -2000,
        right: 2000,
        top: -2000,
        bottom: 2000
      }} style={{
        scale
      }} className="w-[3000px] h-[2000px] flex items-center justify-center relative origin-center" data-id="element-353">
          {/* Edges Layer */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible" data-id="element-354">
            <defs data-id="element-355">
              <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto" data-id="element-356">
                <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" data-id="element-357" />
              </marker>
            </defs>
            {CONNECTIONS.map(([sourceId, targetId], idx) => {
            const source = NODES.find(n => n.id === sourceId);
            const target = NODES.find(n => n.id === targetId);
            if (!source || !target) return null;
            // Calculate center points
            const startX = source.x + 1000; // Offset to center in large canvas
            const startY = source.y + 800;
            const endX = target.x + 1000;
            const endY = target.y + 800;
            // Simple straight lines or elbow connectors could be better, but straight for now
            // Adding some curve
            const midX = (startX + endX) / 2;
            const midY = (startY + endY) / 2;
            return <line key={idx} x1={startX} y1={startY} x2={endX} y2={endY} stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead)" opacity="0.5" data-id="element-358" />;
          })}
          </svg>

          {/* Nodes Layer */}
          {NODES.map(node => <motion.div key={node.id} className={`absolute flex items-center justify-center text-center p-4 rounded-xl shadow-lg border backdrop-blur-sm transition-colors
                ${node.type === 'main' ? 'bg-slate-600/90 border-slate-500 text-white w-40 h-24 font-bold' : node.type === 'action' ? 'bg-emerald-500/90 border-emerald-400 text-white w-36 h-20 font-medium rounded-full' : node.type === 'partner' ? 'bg-amber-400/90 border-amber-300 text-slate-900 w-36 h-20 font-bold rounded-full' : node.type === 'audit' ? 'bg-slate-300/90 border-slate-200 text-slate-900 w-32 h-16 font-medium rounded-full' : 'bg-[#0f172a]/90 border-slate-700 text-slate-300 w-36 h-16 text-sm' // sub
        }
              `} style={{
          left: node.x + 1000 - (node.type === 'main' ? 80 : 72),
          top: node.y + 800 - (node.type === 'main' ? 48 : 32) // Center offset based on height
        }} whileHover={{
          scale: 1.05,
          zIndex: 10
        }} data-id="element-359">
              <span className="whitespace-pre-line" data-id="element-360">{node.label}</span>
            </motion.div>)}
        </motion.div>
      </motion.div>
    </div>;
}